import type {
  AeropayBankAccount,
  AeropayUser,
  CreateAeropayUser,
  Mutate,
} from '@iheartjane/data-access';
import type { User } from '@iheartjane/types';
import { useCallback, useEffect, useState } from 'react';

import { JANE_ERRORS } from '../components/bankLinking/bankLinkingModal/failureScreen/parseError';
import type { MoreInfoFormData } from '../components/bankLinking/bankLinkingModal/moreInfoScreen/moreInfoScreen';

export const SCREEN_NAMES = {
  loading: 'loading',
  moreInfo: 'more-info',
  moreInfoAeropay: 'more-info-for-aeropay',
  deleteBankAccountScreen: 'delete-bank-account',
  linkBank: 'link-bank',
  alreadyEnrolled: 'already-enrolled',
  welcomeBack: 'welcome-back',
  aeropay: 'aeropay',
  success: 'success',
  newAccountSuccess: 'new-account-success',
  promptLinkBank: 'prompt-link-bank',
  error: 'error',
} as const;

export type ScreenName = (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES];

export interface BankLinkingJaneUser {
  user: Pick<
    User,
    | 'email'
    | 'first_name'
    | 'id'
    | 'last_name'
    | 'nickname'
    | 'phone'
    | 'promotions_opt_out'
  >;
}

export type BankLinkingEvent =
  | { email: string; kind: 'unlinkedLastBankAccount'; username: string }
  | {
      email: string;
      isJanePay: boolean;
      kind: 'linkedFirstBankAccount';
      source?: string;
      username: string;
    }
  | {
      email: string;
      isJanePay: boolean;
      kind: 'linkedNewBankAccount';
      source?: string;
      username: string;
    }
  | { email: string; kind: 'linkBankAccountFailed'; username: string };

export interface UseBankLinkingModalDataProps {
  /** True when user is adding an additional bank account */
  addNewAccount: boolean;
  /** Current aeropay user — fetched however the consumer wants */
  aeropayUser: AeropayUser | null | undefined;
  /** Optional error from the aeropay user fetch */
  aeropayUserError?: unknown;
  /** Bank account selected for deletion (drives the delete flow) */
  deleteBankAccount: AeropayBankAccount | null;
  /** Targets Jane Pay flow vs Jane Gold flow */
  isJanePay: boolean;
  /** Current jane user — fetched however the consumer wants */
  janeUser: BankLinkingJaneUser | null | undefined;
  /** Optional error from the jane user fetch */
  janeUserError?: unknown;

  /** Source label, forwarded to tracking events */
  location?: string;
  /** Mark the user as having confirmed Jane Gold enrollment */
  onConfirmJaneGoldUser: () => void;
  /** Create the aeropay user record */
  onCreateAeropayUser: Mutate<CreateAeropayUser, AeropayUser>;
  /** Delete a bank account on the aeropay user */
  onDeleteAeropayBankAccount: Mutate<{ bankAccountId: string | undefined }>;

  /** Called whenever the modal should close */
  onRequestClose: () => void;
  /** Set the selected (default) bank account on the aeropay user */
  onSelectAeropayBankAccount: Mutate<{ bankAccountId: string | undefined }>;
  /** Fan-out tracking — consumer wires moengage / datadog / etc */
  onTrackEvent: (event: BankLinkingEvent) => void;
  /** Update the jane user record */
  onUpdateJaneUser: Mutate<Partial<User>>;
  /** Surface an error to the consumer for rendering */
  setError: (err: unknown) => void;
  /** Start on the JaneGold prompt screen */
  showPrompt?: boolean;
}

const checkAeropayStatus = (aeropayUser: AeropayUser): ScreenName => {
  if (aeropayUser.bank_accounts.length === 0) return SCREEN_NAMES.linkBank;

  if (aeropayUser.requires_gold_confirmation)
    return SCREEN_NAMES.alreadyEnrolled;

  return SCREEN_NAMES.welcomeBack;
};

export const useBankLinkingModalData = ({
  addNewAccount,
  aeropayUser,
  aeropayUserError,
  deleteBankAccount,
  isJanePay,
  janeUser,
  janeUserError,
  location,
  onConfirmJaneGoldUser,
  onCreateAeropayUser,
  onDeleteAeropayBankAccount,
  onRequestClose,
  onSelectAeropayBankAccount,
  onTrackEvent,
  onUpdateJaneUser,
  setError,
  showPrompt,
}: UseBankLinkingModalDataProps) => {
  const [screen, setScreen] = useState<ScreenName>(SCREEN_NAMES.loading);
  const [loaded, setLoaded] = useState(false);
  const [needsMoreFlag, setNeedsMoreFlag] = useState(false);

  const trackIfIdentified = (
    user: BankLinkingJaneUser | null | undefined,
    build: (info: { email: string; username: string }) => BankLinkingEvent
  ) => {
    if (user?.user?.email && user?.user?.nickname) {
      onTrackEvent(
        build({ email: user.user.email, username: user.user.nickname })
      );
    }
  };

  const handleDeleteBankAccount = () => {
    // track only if last bank account deleted
    if (aeropayUser?.bank_accounts.length === 1) {
      trackIfIdentified(janeUser, ({ email, username }) => ({
        kind: 'unlinkedLastBankAccount',
        email,
        username,
      }));
    }

    if (
      aeropayUser &&
      aeropayUser?.bank_accounts.length > 1 &&
      deleteBankAccount?.is_selected
    ) {
      // If the user tries to delete a selected bank account, we need to make sure that
      // another account has been auto-selected and only then proceed with deletion
      const nextBankAccount = aeropayUser?.bank_accounts.filter(
        (bankAccount) =>
          bankAccount.bank_account_id !== deleteBankAccount.bank_account_id
      )[0];
      onSelectAeropayBankAccount(
        {
          bankAccountId: nextBankAccount?.bank_account_id,
        },
        {
          onSuccess: () => {
            onDeleteAeropayBankAccount(
              {
                bankAccountId: deleteBankAccount?.bank_account_id,
              },
              {
                onError: (e: unknown) => {
                  console.error(`Delete Bank account Error: ${e}`);
                  setError(e);
                  setScreen(SCREEN_NAMES.error);
                },
              }
            );
            onRequestClose();
          },

          onError: (e: unknown) => {
            console.error(`Select Bank account Error: ${e}`);
            setError(e);
            setScreen(SCREEN_NAMES.error);
          },
        }
      );
    } else {
      onDeleteAeropayBankAccount(
        {
          bankAccountId: deleteBankAccount?.bank_account_id,
        },
        {
          onSuccess: () => onRequestClose(),
          onError: (e: unknown) => {
            console.error(`Delete Bank account Error: ${e}`);
            setError(e);
            setScreen(SCREEN_NAMES.error);
          },
        }
      );
    }
  };

  const handleClose = () => {
    setError(null);
    if (
      (
        [
          SCREEN_NAMES.success,
          SCREEN_NAMES.newAccountSuccess,
          SCREEN_NAMES.alreadyEnrolled,
        ] as ScreenName[]
      ).includes(screen)
    ) {
      onConfirmJaneGoldUser();

      // track only if first account linked
      if (
        aeropayUser?.bank_accounts.length === 1 &&
        screen !== SCREEN_NAMES.alreadyEnrolled
      ) {
        trackIfIdentified(janeUser, ({ email, username }) => ({
          kind: 'linkedFirstBankAccount',
          email,
          isJanePay,
          source: location,
          username,
        }));
      } else if (screen === SCREEN_NAMES.newAccountSuccess) {
        trackIfIdentified(janeUser, ({ email, username }) => ({
          kind: 'linkedNewBankAccount',
          email,
          isJanePay,
          source: location,
          username,
        }));
      }
    }
    onRequestClose();
  };

  const handleCloseWithOptOut = (optOut?: boolean) => {
    if (optOut !== undefined) {
      onUpdateJaneUser({
        first_name: janeUser?.user.first_name,
        promotions_opt_out: optOut,
      });
    }

    handleClose();
  };

  const handleSubmitInfo = useCallback(
    (data: MoreInfoFormData): void => {
      setScreen(SCREEN_NAMES.loading);
      const { firstName: first_name, lastName: last_name, phone } = data;

      if (screen === SCREEN_NAMES.moreInfoAeropay) {
        onCreateAeropayUser(
          {
            email: janeUser?.user?.email as string,
            first_name,
            last_name,
            phone,
          },
          {
            onSuccess: (data) => {
              const aeropayUser = data;
              if (aeropayUser.bank_accounts?.length) {
                setScreen(
                  aeropayUser.requires_gold_confirmation
                    ? SCREEN_NAMES.alreadyEnrolled
                    : SCREEN_NAMES.welcomeBack
                );
              } else {
                setScreen(SCREEN_NAMES.linkBank);
              }
            },
            onError: (e: unknown) => {
              trackIfIdentified(janeUser, ({ email, username }) => ({
                kind: 'linkBankAccountFailed',
                email,
                username,
              }));
              console.error(`Create Aeropay User Error: ${e}`);
              setError(e);
              setScreen(SCREEN_NAMES.error);
            },
          }
        );
      }

      onUpdateJaneUser(
        {
          first_name,
          last_name,
          phone,
        },
        {
          onSuccess: () => {
            if (screen !== SCREEN_NAMES.moreInfoAeropay && aeropayUser) {
              setScreen(checkAeropayStatus(aeropayUser));
            }
          },
          onError: () => {
            trackIfIdentified(janeUser, ({ email, username }) => ({
              kind: 'linkBankAccountFailed',
              email,
              username,
            }));
            setError(JANE_ERRORS.update);
            setScreen(SCREEN_NAMES.error);
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aeropayUser, screen]
  );

  useEffect(() => {
    if (janeUser?.user?.id && aeropayUser && !loaded && !deleteBankAccount) {
      const needsMoreInfo =
        !janeUser.user.first_name ||
        !janeUser.user.last_name ||
        !janeUser.user.phone;

      // If they do not have an aeropay account yet, we make one,
      // or request more info to do so
      if (!aeropayUser.user_id) {
        if (showPrompt) {
          setScreen(SCREEN_NAMES.promptLinkBank);
        } else if (needsMoreInfo) {
          setScreen(SCREEN_NAMES.moreInfoAeropay);
        } else {
          onCreateAeropayUser(
            {
              email: janeUser.user.email as string,
              first_name: janeUser.user.first_name as string,
              last_name: janeUser.user.last_name as string,
              phone: janeUser.user.phone as string,
            },
            {
              onSuccess: (data) => {
                const aeropayUser = data;
                if (aeropayUser.bank_accounts?.length) {
                  setScreen(
                    aeropayUser.requires_gold_confirmation
                      ? SCREEN_NAMES.alreadyEnrolled
                      : SCREEN_NAMES.welcomeBack
                  );
                } else {
                  setScreen(SCREEN_NAMES.linkBank);
                }
              },
              onError: (e) => {
                setError(e);
                setScreen(SCREEN_NAMES.error);
              },
            }
          );
        }
      } else if (showPrompt) {
        setScreen(SCREEN_NAMES.promptLinkBank);
      } else if (needsMoreInfo) {
        setScreen(SCREEN_NAMES.moreInfo);
      } else {
        setScreen(
          addNewAccount
            ? SCREEN_NAMES.linkBank
            : checkAeropayStatus(aeropayUser)
        );
      }
      setLoaded(true);
      setNeedsMoreFlag(needsMoreInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addNewAccount,
    aeropayUser,
    deleteBankAccount,
    loaded,
    janeUser,
    showPrompt,
  ]);

  useEffect(() => {
    if (deleteBankAccount) {
      setScreen(SCREEN_NAMES.deleteBankAccountScreen);
    }
  }, [deleteBankAccount]);

  useEffect(() => {
    if (janeUserError || aeropayUserError) {
      setError(janeUserError ? JANE_ERRORS.janeUser : aeropayUserError);
      setScreen(SCREEN_NAMES.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [janeUserError, aeropayUserError]);

  return {
    handleClose,
    handleCloseWithOptOut,
    handleSubmitInfo,
    handleDeleteBankAccount,
    needsMoreFlag,
    screen,
    setScreen,
  };
};
